import { Box, Drawer, ListItemText, Paper, Theme, Typography, useMediaQuery, Divider } from '@mui/material';
import { Map, Marker, ZoomControl } from 'pigeon-maps';
import numeral from 'numeral';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Observation } from '../../../shared/types';
import { observationMapViewState } from '../../atoms';
import { useMapView } from '../../hooks/useMapView';
import { ObservationData } from './ObservationData';

const MIN_DRAWER_WIDTH = 300;
const MAX_DRAWER_WIDTH_PERCENT = 0.85;
const DEFAULT_DRAWER_WIDTH_PERCENT = 0.4;

type Marker = { id: string; coordinates: [number, number]; name: string; currentTemp: number; minTemp: number; maxTemp: number };
type MarkerAction = { payload: { id: string | null } };

type Props = {
    observations: Observation[];
};

export const ObservationsMap = ({ observations = [] }: Props) => {
    const [overMakerId, setOverMarkerId] = useState<string | null>(null);
    const [selectedObservation, setSelectedObservation] = useState<Observation | null>(null);
    const { center, zoom, handleBoundsChanged } = useMapView(observationMapViewState, observations?.length ? [observations[0].station.latitude, observations[0].station.longitude] : [39.7283, -121.838]);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const [drawerWidth, setDrawerWidth] = useState(() => window.innerWidth * DEFAULT_DRAWER_WIDTH_PERCENT);
    const isDragging = useRef(false);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            const newWidth = window.innerWidth - e.clientX;
            const maxWidth = window.innerWidth * MAX_DRAWER_WIDTH_PERCENT;
            setDrawerWidth(Math.max(MIN_DRAWER_WIDTH, Math.min(newWidth, maxWidth)));
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    const handleMouseOver = ({ payload = { id: null } }: MarkerAction) => {
        if (payload.id !== overMakerId) {
            setOverMarkerId(payload.id);
        }
    };

    const handleSelectObservation = ({ payload = { id: null } }: MarkerAction) => {
        const observation = observations?.find(({ _id }) => payload.id === _id.toString());

        if (observation) setSelectedObservation(observation);
    };

    const handleCloseObservationDrawer = () => {
        setSelectedObservation(null);
    };

    const markers = useMemo(
        () =>
            observations?.map(
                ({ _id, station, min15 }) =>
                    ({
                        id: _id.toString(),
                        coordinates: [station.latitude, station.longitude],
                        name: station.name,
                        currentTemp: min15.avgTemp,
                        minTemp: min15.runningMinTemp,
                        maxTemp: min15.runningMaxTemp,
                    }) as Marker,
            ),
        [observations],
    );

    useEffect(() => {
        const found = observations.find(({ _id }) => selectedObservation?._id === _id);

        if (found) setSelectedObservation(found);
    }, [observations]);

    return (
        <>
            <Box className="pigeon-map-container" sx={{ display: 'flex', height: '100%' }}>
                <Box className="map-wrapper relative" style={{ flex: 1 }}>
                    <Map
                        center={center}
                        zoom={zoom}
                        onBoundsChanged={handleBoundsChanged}>
                        <ZoomControl />
                        {markers.map(({ id, coordinates, name, maxTemp, minTemp, currentTemp }) => {
                            const selectedId = selectedObservation?._id.toString();
                            let zIndex = id === overMakerId ? 2 : 1;

                            zIndex = id === selectedId ? 3 : zIndex;

                            return (
                                <Marker key={id} width={50} height={45} anchor={coordinates} onClick={handleSelectObservation} onMouseOver={handleMouseOver} hover={false} payload={{ id }} style={{ zIndex }}>
                                    <Paper
                                        elevation={0}
                                        sx={(theme) => {
                                            const borderColor = id === selectedId ? theme.palette.info.main : theme.palette.background.paper;

                                            return {
                                                pointerEvents: 'all',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                                p: 0,
                                                borderWidth: 1,
                                                borderStyle: 'solid',
                                                borderColor,
                                                zIndex: 1,
                                                '&::before': {
                                                    content: '""',
                                                    display: 'block',
                                                    position: 'absolute',
                                                    bottom: -10,
                                                    right: 'calc(50% - 5px)',
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: theme.palette.background.paper,
                                                    transform: 'translateY(-50%) rotate(45deg)',
                                                    zIndex: 0,
                                                    borderWidth: 1,
                                                    borderStyle: 'solid',
                                                    borderLeftColor: theme.palette.background.paper,
                                                    borderTopColor: theme.palette.background.paper,
                                                    borderRightColor: borderColor,
                                                    borderBottomColor: borderColor,
                                                },
                                            };
                                        }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', p: 0.5 }}>
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="subtitle1">{`${name} ${numeral(currentTemp).format('0')}`}&#8457;</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                                                <Typography variant="subtitle2" color="error">
                                                    {numeral(maxTemp).format('0')}&#8457;
                                                </Typography>
                                                <Divider orientation="vertical" flexItem={true} />
                                                <Typography variant="subtitle2" color="info">
                                                    {numeral(minTemp).format('0')}&#8457;
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Marker>
                            );
                        })}
                    </Map>
                </Box>
            </Box>
            {selectedObservation && (
                <Drawer
                    variant={isNotSm ? 'permanent' : 'temporary'}
                    anchor="right"
                    open={Boolean(selectedObservation)}
                    hideBackdrop={true}
                    sx={{ '& .MuiDrawer-paper': { width: isNotSm ? drawerWidth : '100%' } }}
                    slotProps={{
                        paper: { sx: (theme) => ({ backgroundColor: theme.palette.background.paper, top: isNotSm ? 156 : undefined, pb: isNotSm ? '156px' : undefined }) },
                    }}>
                    {isNotSm && (
                        <Box
                            onMouseDown={handleMouseDown}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: 6,
                                cursor: 'col-resize',
                                zIndex: 1,
                                '&:hover': { backgroundColor: 'primary.main', opacity: 0.3 },
                            }}
                        />
                    )}
                    <ObservationData observation={selectedObservation} onClose={handleCloseObservationDrawer} />
                </Drawer>
            )}
        </>
    );
};
